import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Bot,
  User,
  Send,
  Plus,
  X,
  AlertCircle,
  Loader2,
  MessageSquare,
  Mail,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MainHeader from "../MainHeader";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

export default function DeepResearchChat() {
  const { api } = useAuth(); // baseURL already includes "/api/v1"
  const navigate = useNavigate();

  // URL 파라미터에서 session, qt 값 가져오기
  const [queryType, setQueryType] = useState("single_turn"); // 'single_turn' or 'multi_turn'
  const [chatId, setChatId] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qt = params.get("qt");
    const session = params.get("session");
    if (qt === "multi_turn") {
      setQueryType("multi_turn");
    }
    if (session) {
      setChatId(session);
    } else {
      // 만약 session이 없으면 뒤로 돌리기
      navigate(-1);
    }
  }, [navigate]);

  // Single-turn 상태들
  const [questions, setQuestions] = useState([{ id: 1, text: "" }]);
  const [aiResponse, setAiResponse] = useState("");
  const [isLoadingSingle, setIsLoadingSingle] = useState(false);
  const [singleAsked, setSingleAsked] = useState(false);
  const [singleError, setSingleError] = useState(false); // 오류 상태

  // Multi-turn 상태들
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isLoadingMulti, setIsLoadingMulti] = useState(false);
  const [multiError, setMultiError] = useState(false); // 오류 상태

  // 채팅이 종료되었는지 여부
  const [chatEnded, setChatEnded] = useState(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Single-turn 함수들
  const addQuestion = () => {
    setQuestions((prev) => [...prev, { id: Date.now(), text: "" }]);
  };
  const removeQuestion = (id) => {
    if (questions.length > 1) {
      setQuestions((prev) => prev.filter((q) => q.id !== id));
    }
  };
  const updateQuestion = (id, text) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, text } : q)));
  };
  const hasValidQuestions = questions.some((q) => q.text.trim() !== "");

  const submitQuestions = async () => {
    if (!hasValidQuestions || chatEnded) return;
    setIsLoadingSingle(true);
    setSingleError(false);

    // Q1. Q2. 붙이고 줄바꿈
    const nonEmpty = questions.filter((q) => q.text.trim());
    const combined = nonEmpty
      .map((q, idx) => `Q${idx + 1}. ${q.text.trim()}`)
      .join("\n");

    try {
      const response = await api.post("/deep_research/chat/completions", {
        chat_id: chatId,
        chat_message: combined,
      });
      const { generated_content, data_save } = response.data;

      if (data_save === false) {
        // 저장 실패 시
        setAiResponse("");
        setSingleError(true);
        setChatEnded(true);
      } else {
        setAiResponse(generated_content || "");
      }
    } catch (err) {
      console.error("Single-turn 오류:", err);
      setAiResponse("");
      setSingleError(true);
      setChatEnded(true);
    } finally {
      setIsLoadingSingle(false);
      setSingleAsked(true);
    }
  };

  // Multi-turn 함수들
  const sendMessage = async () => {
    if (!currentMessage.trim() || isLoadingMulti || chatEnded) return;
    const userMessage = {
      id: Date.now(),
      type: "user",
      content: currentMessage.trim(),
      timestamp: new Date().toLocaleTimeString("ko-KR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, userMessage]);
    setCurrentMessage("");
    setIsLoadingMulti(true);
    setMultiError(false);

    try {
      const response = await api.post("/deep_research/chat/completions", {
        chat_id: chatId,
        chat_message: userMessage.content,
      });
      const { generated_content, data_save } = response.data;
      if (data_save === false) {
        const aiMsg = {
          id: Date.now() + 1,
          type: "assistant",
          content: "",
          timestamp: new Date().toLocaleTimeString("ko-KR", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, aiMsg]);
        setMultiError(true);
        setChatEnded(true);
      } else {
        const aiMsg = {
          id: Date.now() + 1,
          type: "assistant",
          content: generated_content || "",
          timestamp: new Date().toLocaleTimeString("ko-KR", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, aiMsg]);
      }
    } catch (err) {
      console.error("Multi-turn 오류:", err);
      const aiMsg = {
        id: Date.now() + 1,
        type: "assistant",
        content: "",
        timestamp: new Date().toLocaleTimeString("ko-KR", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages((prev) => [...prev, aiMsg]);
      setMultiError(true);
      setChatEnded(true);
    } finally {
      setIsLoadingMulti(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // 채팅 종료 버튼
  const shutdownChat = async () => {
    if (!chatId) return;
    try {
      await api.post("/deep_research/chat/shutdown", { chat_id: chatId });
      navigate("/deep_research");
    } catch (err) {
      console.error("Shutdown 오류:", err);
      // 실패해도 우선 채팅 종료 처리
    } finally {
      setChatEnded(true);
    }
  };

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 relative overflow-hidden">
        {/* 배경 블러 + 그리드 패턴 */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-400/30 rounded-full filter blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-[500px] h-[500px] bg-purple-400/30 rounded-full filter blur-[120px] animate-pulse delay-700"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-400/20 rounded-full filter blur-[100px] animate-pulse delay-1000"></div>
          <div
            className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http://www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"
            opacity-20
          ></div>
        </div>

        <div className="relative z-10 h-screen flex flex-col">
          {/* 헤더 */}
          <div className="px-6 py-6 border-b border-white/10 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.history.back()}
                className="group flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                돌아가기
              </button>
              <div className="w-px h-6 bg-white/20"></div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-lg rounded-xl flex items-center justify-center">
                    {queryType === "single_turn" ? (
                      <Mail className="w-5 h-5 text-white" />
                    ) : (
                      <MessageSquare className="w-5 h-5 text-white" />
                    )}
                  </div>
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-white">
                    {queryType === "single_turn" ? "1회성 질문" : "멀티턴 대화"}
                  </h1>
                  <p className="text-sm text-blue-100">문서 분석 중</p>
                </div>
              </div>
            </div>
            {!chatEnded && (
              <button
                onClick={shutdownChat}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl transition-colors"
              >
                채팅 종료
              </button>
            )}
          </div>

          {/* 메인 컨텐츠 */}
          <div className="flex-1 overflow-hidden">
            {chatEnded ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                  <p className="text-white/80">채팅이 종료되었습니다.</p>
                </div>
              </div>
            ) : queryType === "single_turn" ? (
              // Single Turn UI
              <div className="h-full max-w-7xl mx-auto px-6 py-6 overflow-hidden">
                <div className="grid md:grid-cols-2 gap-6 h-full">
                  {/* 질문 입력 영역 */}
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 flex flex-col h-full">
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      질문 작성
                    </h2>
                    <div className="flex-1 overflow-auto space-y-3 mb-4">
                      {questions.map((question, index) => (
                        <div key={question.id} className="relative">
                          <textarea
                            value={question.text}
                            onChange={(e) =>
                              updateQuestion(question.id, e.target.value)
                            }
                            placeholder={`질문 ${index + 1}을 입력하세요...`}
                            className="w-full p-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/40 focus:bg-white/20 transition-all resize-none"
                            rows={3}
                            disabled={singleAsked}
                          />
                          {questions.length > 1 && !singleAsked && (
                            <button
                              onClick={() => removeQuestion(question.id)}
                              className="absolute top-2 right-2 p-1 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                            >
                              <X className="w-4 h-4 text-red-300" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={addQuestion}
                        disabled={isLoadingSingle || singleAsked}
                        className={`flex items-center gap-2 px-4 py-2 border border-white/20 rounded-xl transition-all ${
                          !isLoadingSingle && !singleAsked
                            ? "bg-white/10 hover:bg-white/20 text-white"
                            : "bg-white/5 text-white/40 cursor-not-allowed"
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                        질문 추가
                      </button>
                      <button
                        onClick={submitQuestions}
                        disabled={
                          !hasValidQuestions || isLoadingSingle || singleAsked
                        }
                        className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all ${
                          hasValidQuestions && !isLoadingSingle && !singleAsked
                            ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600"
                            : "bg-white/10 text-white/40 cursor-not-allowed"
                        }`}
                      >
                        {singleAsked ? (
                          "질문에 대한 답변이 완료되었습니다"
                        ) : isLoadingSingle ? (
                          <div className="flex items-center justify-center gap-2 text-white">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            응답을 생성하고 있습니다. 문서 길이에 따라 시간이
                            소요될 수 있습니다.
                          </div>
                        ) : (
                          "한번에 질문하기"
                        )}
                      </button>
                    </div>
                  </div>

                  {/* AI 답변 영역 - 수정됨 */}
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 flex flex-col h-full overflow-hidden">
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Bot className="w-5 h-5" />
                      AI 답변
                    </h2>
                    <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2">
                      {isLoadingSingle ? (
                        <div className="flex items-center justify-center h-full text-white">
                          <Loader2 className="w-6 h-6 animate-spin mr-2" />
                          응답을 생성하고 있습니다. 문서 길이에 따라 시간이
                          소요될 수 있습니다.
                        </div>
                      ) : singleError ? (
                        <div className="flex items-center justify-center h-full text-red-400">
                          응답 생성에 실패했습니다.
                        </div>
                      ) : aiResponse ? (
                        <div className="prose prose-invert max-w-none prose-sm prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2 prose-li:my-1 prose-ul:my-2 prose-ol:my-2">
                          <div className="text-white/90">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({ node, ...props }) => (
                                  <h1
                                    className="text-2xl font-bold text-white mb-3 mt-4"
                                    {...props}
                                  />
                                ),
                                h2: ({ node, ...props }) => (
                                  <h2
                                    className="text-xl font-semibold text-white mb-2 mt-3"
                                    {...props}
                                  />
                                ),
                                h3: ({ node, ...props }) => (
                                  <h3
                                    className="text-lg font-medium text-white mb-2 mt-3"
                                    {...props}
                                  />
                                ),
                                p: ({ node, ...props }) => (
                                  <p
                                    className="text-white/90 leading-relaxed mb-3"
                                    {...props}
                                  />
                                ),
                                ul: ({ node, ...props }) => (
                                  <ul
                                    className="list-disc list-inside text-white/90 space-y-1 mb-3"
                                    {...props}
                                  />
                                ),
                                ol: ({ node, ...props }) => (
                                  <ol
                                    className="list-decimal list-inside text-white/90 space-y-1 mb-3"
                                    {...props}
                                  />
                                ),
                                li: ({ node, ...props }) => (
                                  <li className="text-white/90" {...props} />
                                ),
                                blockquote: ({ node, ...props }) => (
                                  <blockquote
                                    className="border-l-4 border-white/30 pl-4 my-3 text-white/80 italic"
                                    {...props}
                                  />
                                ),
                                code: ({ node, inline, ...props }) =>
                                  inline ? (
                                    <code
                                      className="bg-white/10 px-1.5 py-0.5 rounded text-sm text-white/90"
                                      {...props}
                                    />
                                  ) : (
                                    <code
                                      className="block bg-black/30 p-3 rounded-lg overflow-x-auto text-sm text-white/90 my-3"
                                      {...props}
                                    />
                                  ),
                                pre: ({ node, ...props }) => (
                                  <pre
                                    className="bg-black/30 p-3 rounded-lg overflow-x-auto my-3"
                                    {...props}
                                  />
                                ),
                                table: ({ node, ...props }) => (
                                  <div className="overflow-x-auto my-3">
                                    <table
                                      className="min-w-full border-collapse border border-white/20"
                                      {...props}
                                    />
                                  </div>
                                ),
                                th: ({ node, ...props }) => (
                                  <th
                                    className="border border-white/20 px-3 py-2 bg-white/10 text-white font-semibold"
                                    {...props}
                                  />
                                ),
                                td: ({ node, ...props }) => (
                                  <td
                                    className="border border-white/20 px-3 py-2 text-white/90"
                                    {...props}
                                  />
                                ),
                                hr: ({ node, ...props }) => (
                                  <hr
                                    className="border-white/20 my-4"
                                    {...props}
                                  />
                                ),
                                a: ({ node, ...props }) => (
                                  <a
                                    className="text-blue-300 underline hover:text-blue-200"
                                    {...props}
                                  />
                                ),
                                strong: ({ node, ...props }) => (
                                  <strong
                                    className="font-semibold text-white"
                                    {...props}
                                  />
                                ),
                                em: ({ node, ...props }) => (
                                  <em
                                    className="italic text-white/90"
                                    {...props}
                                  />
                                ),
                              }}
                            >
                              {aiResponse}
                            </ReactMarkdown>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <Bot className="w-16 h-16 text-white/20 mx-auto mb-4" />
                            <p className="text-white/60">
                              질문을 작성하고 제출하면 AI가 답변합니다
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Multi Turn UI
              <div className="h-full max-w-4xl mx-auto px-6 py-6 flex flex-col overflow-hidden">
                <div className="flex-1 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 flex flex-col h-full">
                  {/* 채팅 메시지 영역 */}
                  <div className="flex-1 overflow-auto mb-4">
                    {messages.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <Bot className="w-16 h-16 text-white/20 mx-auto mb-4" />
                          <p className="text-white/60">
                            문서에 대해 궁금한 점을 물어보세요
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex gap-3 ${
                              message.type === "user"
                                ? "justify-end"
                                : "justify-start"
                            }`}
                          >
                            {message.type === "assistant" && (
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-400 rounded-lg flex items-center justify-center">
                                  <Bot className="w-5 h-5 text-white" />
                                </div>
                              </div>
                            )}

                            <div
                              className={`max-w-[70%] ${
                                message.type === "user" ? "text-right" : ""
                              }`}
                            >
                              <div
                                className={`inline-block p-4 rounded-2xl ${
                                  message.type === "user"
                                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white"
                                    : "bg-white/10 backdrop-blur text-white"
                                }`}
                              >
                                <p className="whitespace-pre-wrap">
                                  {message.content}
                                </p>
                              </div>
                              <p className="text-xs text-blue-200 mt-1 px-2">
                                {message.timestamp}
                              </p>
                            </div>

                            {message.type === "user" && (
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                  <User className="w-5 h-5 text-white" />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {isLoadingMulti && (
                          <div className="flex items-center justify-center h-full text-white">
                            <Loader2 className="w-6 h-6 animate-spin mr-2" />
                            응답을 생성하고 있습니다. 문서 길이에 따라 시간이
                            소요될 수 있습니다.
                          </div>
                        )}
                        {multiError && !isLoadingMulti && (
                          <div className="flex items-center justify-center h-full text-red-400">
                            응답 생성에 실패했습니다.
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>

                  {/* 입력창 */}
                  <div className="border-t border-white/20 pt-4">
                    <div className="flex gap-3">
                      <textarea
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="메시지를 입력하세요..."
                        className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/40 focus:bg-white/20 transition-all resize-none"
                        rows={2}
                        disabled={chatEnded}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={
                          !currentMessage.trim() || isLoadingMulti || chatEnded
                        }
                        className={`px-6 py-3 rounded-xl transition-all ${
                          currentMessage.trim() && !isLoadingMulti && !chatEnded
                            ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600"
                            : "bg-white/10 text-white/40 cursor-not-allowed"
                        }`}
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 하단 경고 메시지 */}
          <div className="px-6 py-4 border-t border-white/10">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm text-yellow-300">
              <AlertCircle className="w-4 h-4" />
              <span>
                Ash는 실수를 할 수 있습니다. 중요한 정보는 재차 확인하세요.
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
